import { useCallback, useEffect, useRef, useState } from 'react'

export type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'stopped' | 'error'

export interface AudioRecorderState {
  status: RecorderStatus
  elapsed: number
  dataUrl: string | null
  error: string | null
  isSupported: boolean
}

export interface AudioRecorderControls {
  start: () => Promise<void>
  stop: () => void
  reset: () => void
}

export type AudioRecorder = AudioRecorderState & AudioRecorderControls

const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  for (const t of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(t)) return t
  }
  return ''
}

export function useAudioRecorder(maxDurationSeconds = 120): AudioRecorder {
  const [status, setStatus] = useState<RecorderStatus>('idle')
  const [elapsed, setElapsed] = useState(0)
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'

  const mrRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const elapsedRef = useRef(0)

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }, [])

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  // Stop everything and free resources, but preserve state (status/dataUrl).
  const teardown = useCallback(() => {
    stopTimer()
    releaseStream()
    mrRef.current = null
    elapsedRef.current = 0
  }, [stopTimer, releaseStream])

  useEffect(() => () => teardown(), [teardown])

  const start = useCallback(async () => {
    if (!isSupported) {
      setError('Audio recording is not supported in this browser.')
      setStatus('error')
      return
    }
    setStatus('requesting')
    setError(null)
    setDataUrl(null)
    setElapsed(0)
    elapsedRef.current = 0
    chunksRef.current = []

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    } catch (err) {
      const denied = err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')
      setError(
        denied
          ? 'Microphone permission denied. Enable it in browser settings or upload a file.'
          : 'Could not access microphone. Try uploading a file instead.',
      )
      setStatus('error')
      return
    }

    streamRef.current = stream
    chunksRef.current = []

    const mimeType = pickMimeType()
    const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    mrRef.current = mr

    mr.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }

    mr.onstop = () => {
      const savedChunks = [...chunksRef.current]
      const resolvedMime = mr.mimeType || 'audio/webm'
      teardown()
      const blob = new Blob(savedChunks, { type: resolvedMime })
      const reader = new FileReader()
      reader.onload = (e2) => {
        setDataUrl(e2.target?.result as string)
        setStatus('stopped')
      }
      reader.onerror = () => {
        setError('Could not encode the recording. Try again.')
        setStatus('error')
      }
      reader.readAsDataURL(blob)
    }

    mr.start(100)
    setStatus('recording')

    timerRef.current = setInterval(() => {
      elapsedRef.current += 1
      setElapsed(elapsedRef.current)
      if (elapsedRef.current >= maxDurationSeconds) {
        mr.stop()
        stopTimer()
      }
    }, 1000)
  }, [isSupported, maxDurationSeconds, teardown, stopTimer])

  const stop = useCallback(() => {
    if (mrRef.current && mrRef.current.state === 'recording') {
      mrRef.current.stop()
    }
    stopTimer()
  }, [stopTimer])

  const reset = useCallback(() => {
    teardown()
    setStatus('idle')
    setElapsed(0)
    setDataUrl(null)
    setError(null)
    chunksRef.current = []
  }, [teardown])

  return { status, elapsed, dataUrl, error, isSupported, start, stop, reset }
}
